/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import css from './calendar.module.css';
import Cell from './Cell';
import ValueFilter from '../../../../../../components/common/table/value-filter/value-filter';
import { useTranslation } from 'react-i18next';
import { route } from '../../../../../../services/utils';
import { LIVESTOCK_ANIMALS_AND_EVENTS_INDEX } from '../../../../../../router/routes';
import { useHistory, useParams } from 'react-router-dom';
import AnimalsAndEventsTabKeys from '../../../enums/animals-and-events-tab-keys';
import { useQuery } from '../../../../../../hooks/use-query';
import DateFilter, {
  DateFields,
} from '../../../../../../components/common/table/date-filter/date-filter';
import './calendar.css';
import { AnimalEvent } from '../../../../../../interfaces/animal-event';
import 'moment/locale/lv';

interface Props {
  data?: AnimalEvent[];
  furthestYear?: number;
}

const SHORT_NAME_OF_WEEK_DAY = [
  { key: 'Mon', count: 0 },
  { key: 'Tue', count: 1 },
  { key: 'Wed', count: 2 },
  { key: 'Thu', count: 3 },
  { key: 'Fri', count: 4 },
  { key: 'Sat', count: 5 },
  { key: 'Sun', count: 6 },
];

const LAST_DAY_OF_THE_WEEK_INDEX = 6;

const CALENDAR_START_YEAR = 2000;

const DAYS_IN_A_WEEK = 7;

const MONTHS_IN_A_YEAR = 12;

interface DayData {
  day: number;
  isOutside: boolean;
  isActive: boolean;
  events: AnimalEvent[];
  frame?: string;
  month?: number;
}

interface AnimalEventAndFrame {
  events: AnimalEvent[];
  frame?: string;
}

const Calendar = ({ data, furthestYear }: Props) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDate = new Date().getDate();
  const history = useHistory();
  const query = useQuery();
  const {
    year = '',
    month = String(currentMonth).padStart(2, '0'),
    date = JSON.stringify(new Date()).slice(1, 11),
    mode = 'month',
    statuses = '',
  } = query[0]!;

  const { farmId } = useParams<{ farmId: string }>();
  const { t } = useTranslation();
  const yearWithValues = [{ value: '', label: t('year') }];
  for (let year = furthestYear; year >= CALENDAR_START_YEAR; year--) {
    yearWithValues.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  const selectedYear = !!year ? year : currentYear;
  const selectedDate = (selectedYear === currentYear && Number(month) === currentMonth) ? currentDate : '01';
  const [calendarDate, setCalendarDate] = useState<Moment>(moment(`${selectedYear}-${month}-${selectedDate}`, 'YYYY-MM-DD'));
  const [, setReload] = useState(0);
  const [monthWithValues, setMonthWithValues] = useState([]);
  const calculationDate = moment(calendarDate);
  const numDays = calculationDate.daysInMonth();
  const prefixDays = calculationDate.startOf('month').day() - 1;
  const suffixDays = DAYS_IN_A_WEEK - calculationDate.endOf('month').day();

  const prevMonthDays = moment(calendarDate)
    .subtract(1, 'months')
    .daysInMonth();
  const isMonthMode = mode === 'month';
  const arrayLength = numDays;
  const dateFilterName = 'animal-events-date';

  const [weekDict] = useState([
    t('pages.livestock.animals_and_events.events.short_monday'),
    t('pages.livestock.animals_and_events.events.short_tuesday'),
    t('pages.livestock.animals_and_events.events.short_wednesday'),
    t('pages.livestock.animals_and_events.events.short_thursday'),
    t('pages.livestock.animals_and_events.events.short_friday'),
    t('pages.livestock.animals_and_events.events.short_saturday'),
    t('pages.livestock.animals_and_events.events.short_sunday'),
  ]);

  const getMonthName = useCallback((monthNumber: number) => {
    const monthName = moment.months()[monthNumber];

    return monthName
      ? monthName.charAt(0).toUpperCase() + monthName.slice(1)
      : null;
  }, []);

  const changeMode = useCallback(
    (mode?: string) => {
      history.replace(
        route(
          LIVESTOCK_ANIMALS_AND_EVENTS_INDEX.concat(
            '?year=:year&month=:month&mode=:mode&statuses=:statuses'
          ),
          {
            farmId,
            tab: AnimalsAndEventsTabKeys.events,
            month: month,
            year: year,
            mode: mode,
            statuses: statuses,
          }
        )
      );
    },
    [history, statuses, date, farmId, month, year]
  );
  const changeDateTime = useCallback(
    (year?: string, month?: string, date?: string) => {
      history.replace(
        route(
          LIVESTOCK_ANIMALS_AND_EVENTS_INDEX.concat(
            '?year=:year&month=:month&mode=:mode&statuses=:statuses'
          ),
          {
            farmId,
            tab: AnimalsAndEventsTabKeys.events,
            year: year,
            month: month,
            mode: mode,
            statuses: statuses,
          }
        )
      );
    },
    [history, mode, statuses, farmId]
  );

  const changeCalendarDate = useCallback((calendarDate: Moment) => {
    if (!calendarDate) return;
    setReload((old) => old + 1);
    setCalendarDate(calendarDate);
  }, []);

  const getAnimalEvents = (day: number): AnimalEventAndFrame => {
    const yearChanged = !year || year === '' ? currentYear : year;
    const animalEvents: AnimalEvent[] = [];
    let index: number = 0;
    let missedIndex: number = 0;
    const todayOnlyDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    data.forEach((animalEvent) => {
      if (
        animalEvent.date.substring(0, 10) ===
        `${yearChanged}-${String(month).padStart(2, '0')}-${String(
          day
        ).padStart(2, '0')}`
      ) {
        animalEvents.push(animalEvent);
        if (
          String(animalEvent.status) === '0' &&
          new Date(animalEvent.date) < todayOnlyDate
        ) {
          missedIndex = missedIndex + 1;
        }
        index = index + 1;
      }
    });

    if (index === missedIndex && index !== 0) {
      return {
        events: animalEvents,
        frame: 'missed',
      };
    }
    return {
      events: animalEvents,
      frame: null,
    };
  };

  const isSameDate = (day: number) => {
    const yearChanged = !year || year === '' ? new Date().getFullYear() : year;
    return (
      String(
        `${yearChanged}-${String(month).padStart(2, '0')}-${String(
          day
        ).padStart(2, '0')}`
      ) === String(date)
    );
  };

  useEffect(() => {
    const setCurrentYear = () =>
      !year || year === '' ? `- ${currentYear}` : `- ${year}`;

    const monthWithValues = [];
    for (let i = 0; i < MONTHS_IN_A_YEAR; i++) {
      const monthName = getMonthName(i);
      const monthLabel = `${monthName} ${setCurrentYear()}`;

      monthWithValues.push({
        value: (i + 1).toString().padStart(2, '0'),
        label: monthLabel,
      });
    }

    setMonthWithValues(monthWithValues);

    if (year) {
      const yearChanged = !year || year === '' ? currentYear : year;
      changeCalendarDate(
        calendarDate.set('year', year === '' ? currentYear : year)
      );
      if (
        year.toString() === currentYear.toString() ||
        year.toString() === ''
      ) {
        changeDateTime(
          year,
          currentMonth.toString().padStart(2, '0'),
          `${yearChanged}-${currentMonth.toString().padStart(2, '0')}-01`
        );
      } else {
        changeDateTime(year, '01', `${year}-01-01`);
      }
    }
  }, [
    year,
    getMonthName,
    changeCalendarDate,
    changeDateTime,
    currentYear,
    calendarDate,
  ]);

  useEffect(() => {
    if (month) {
      const yearChanged = !year || year === '' ? currentYear : year;
      changeCalendarDate(calendarDate.set('month', Number(month) - 1));
      changeDateTime(
        year,
        month,
        `${yearChanged}-${String(month).padStart(2, '0')}-01`
      );
    }
  }, [month, changeCalendarDate, changeDateTime, calendarDate]);

  const fullDaysAndData = useMemo<DayData[]>(() => {
    const prevMonthDay = Array.from({
      length: prefixDays === -1 ? LAST_DAY_OF_THE_WEEK_INDEX : prefixDays,
    }).map((_, index) => ({
      day:
        prevMonthDays -
        (prefixDays === -1 ? LAST_DAY_OF_THE_WEEK_INDEX : prefixDays) +
        index +
        1,
      isOutside: true,
      isActive: false,
      events: [],
      frame: null,
    }));

    const currentMonthDay = Array.from({ length: arrayLength }).map(
      (_, index) => {
        const data = getAnimalEvents(index + 1);
        return {
          day: index + 1,
          month: Number(month),
          isOutside: false,
          isActive: isSameDate(index + 1),
          events: data.events,
          frame: data.frame,
        };
      }
    );

    const nextMonthDay = isMonthMode
      ? Array.from({ length: suffixDays }).map((_, index) => ({
          day: index + 1,
          isOutside: true,
          isActive: false,
          events: [],
          frame: null,
        }))
      : [];

    if (mode === 'week') {
      let weekIdx = 0;
      const activeDay = calendarDate.date();
      const days = [...prevMonthDay, ...currentMonthDay, ...nextMonthDay].map(
        (el: DayData) => {
          let cell = { ...el, week: { key: '', count: 0 } };
          if (weekIdx < DAYS_IN_A_WEEK) {
            cell = { ...el, week: SHORT_NAME_OF_WEEK_DAY[weekIdx] };
            weekIdx += 1;
          } else {
            weekIdx = 0;
            cell = { ...el, week: SHORT_NAME_OF_WEEK_DAY[weekIdx] };
            weekIdx = 1;
          }
          return cell;
        }
      );
      weekIdx = 0;
      const idx = days.findIndex((cell: DayData) => (cell.day === activeDay && cell?.month === Number(month)));
      const endIdx = idx - days[idx].week.count + DAYS_IN_A_WEEK;

      return days.slice(idx - days[idx].week.count, endIdx);
    }
    return [...prevMonthDay, ...currentMonthDay, ...nextMonthDay];
  }, [prefixDays, arrayLength, suffixDays, date, month, year, mode]);

  return (
    <div className={'event-calendar'}>
      <div className={css['event-bar']}>
        <div className={css['month-change-container']}>
          <div className={'col-span-2'}>
            <ValueFilter
              className={css['month-change-item']}
              name={'month'}
              options={monthWithValues}
              customClassName={'month-select'}
              isClearable={false}
            />
          </div>
          <div className={'flex items-center'}>
            <DateFilter
              name={dateFilterName}
              fields={[DateFields.date]}
              showDateControls={true}
              inline
            />
          </div>
        </div>
        <div className={css['calendar-status-container']}>
          <div className={css['calendar-status-item']}>
            <button
              className={mode === 'week' ? css['active'] : 'text-[#747474]'}
              onClick={() => changeMode('week')}
            >
              {t('classifiers.week')}
            </button>
          </div>
          <div className={css['calendar-status-item']}>
            <button
              className={mode === 'month' ? css['active'] : 'text-[#747474]'}
              onClick={() => changeMode('month')}
            >
              {t('classifiers.month')}
            </button>
          </div>
          <div className={css['calendar-status-item']}>
            <ValueFilter
              name={'year'}
              options={yearWithValues}
              customClassName={'year-select'}
            />
          </div>
        </div>
      </div>
      <div className={css['calendar-wrapper']}>
        <div className={css['calendar']}>
          {
            <div className={css['cells']}>
              <>
                {fullDaysAndData.map((dayData, index) => (
                  <Cell
                    className={`${css['day-cell']} ${
                      dayData.isOutside && css['outside-day']
                    }`}
                    key={index}
                    day={dayData.day}
                    isActive={dayData.isActive}
                    events={dayData.events}
                    hasWeek={index < DAYS_IN_A_WEEK}
                    weekName={index < DAYS_IN_A_WEEK ? weekDict[index] : ''}
                    frame={dayData.frame}
                  />
                ))}
              </>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

export default Calendar;
